import { useState, useEffect, Fragment } from "react";
import { motion } from "framer-motion";
import { X, Loader, Eye, EyeOff, Download, BarChart2, Trash2, AlertCircle } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmDialog";
import { formatDate } from "../utils/dates";
import { calculatedColumnLabel, calculateDateValue } from "../utils/dateCalculations";

// Los campos de fecha con cálculo automático (edad, antigüedad...) muestran
// el resultado en una columna propia, junto a la columna de la fecha. Se
// calcula respecto a la fecha de envío para que no cambie con el tiempo.
function hasDateCalculation(field) {
  return field.type === "date" && !!field.dateCalculation;
}

function maskValue(value) {
  const str = String(value ?? "");
  if (str.length <= 3) return "•".repeat(str.length || 4);
  return "•".repeat(str.length - 3) + str.slice(-3);
}

function CellValue({ value, field, revealed, onToggle }) {
  if (value === undefined || value === null || value === "") return <span style={{ color: "var(--text-muted)" }}>—</span>;

  if (field.type === "file" && typeof value === "object" && "originalname" in value) {
    return (
      <a
        href={api.fileUrl(value.filename)}
        target="_blank"
        rel="noreferrer"
        style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--primary)", fontSize: "0.8rem" }}
      >
        <Download size={13} /> {value.originalname}
      </a>
    );
  }

  const normalizedValue = field.type === "date" && value && typeof value === "object" && "value" in value
    ? value.value
    : value;

  if (field.type === "boolean") return <span>{normalizedValue ? "Si" : "No"}</span>;

  if (field.sensitive) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {revealed ? String(normalizedValue) : maskValue(normalizedValue)}
        <button onClick={onToggle} className="btn-icon" style={{ width: 22, height: 22 }} title={revealed ? "Ocultar" : "Mostrar"}>
          {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
        </button>
      </span>
    );
  }

  return <span>{String(normalizedValue)}</span>;
}

export default function FormResponsesModal({ formId, formName, companyId, onClose, onChanged }) {
  const { isAdmin } = useAuth();
  const { confirm, ConfirmUI } = useConfirm();
  const [fields, setFields] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getFormResponses(formId, companyId).then((data) => {
      setFields(data?.fields || []);
      setResponses(data?.responses || []);
      setLoading(false);
    });
  }, [formId, companyId]);

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSelected = responses.length > 0 && selected.size === responses.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(responses.map((response) => response.id)));
  };

  const deleteResponses = async (ids) => {
    const ok = await confirm({
      title: ids.length === 1 ? "Eliminar respuesta" : `Eliminar ${ids.length} respuestas`,
      message: "Las respuestas y sus archivos adjuntos se eliminarán de forma permanente. El formulario y su enlace seguirán activos.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;

    setDeleting(true);
    setError(null);
    const result = await api.deleteFormResponses(formId, ids).catch(() => ({ error: "No se pudo conectar con el servidor" }));
    setDeleting(false);
    if (result.error) { setError(result.error); return; }

    const removed = new Set(ids);
    setResponses((prev) => prev.filter((response) => !removed.has(response.id)));
    setSelected((prev) => new Set([...prev].filter((id) => !removed.has(id))));
    onChanged && onChanged();
  };

  const toggleReveal = (key) => {
    setRevealed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <motion.div
        className="modal modal-large"
        style={{ maxWidth: 960 }}
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}
      >
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "#F0FDFA", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BarChart2 size={18} style={{ color: "var(--primary)" }} />
            </div>
            <div>
              <h2>Respuestas{formName ? ` - ${formName}` : ""}</h2>
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                {responses.length} respuesta{responses.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isAdmin && selected.size > 0 && (
              <button className="btn-secondary" onClick={() => deleteResponses([...selected])} disabled={deleting}
                style={{ color: "#DC2626", display: "inline-flex", alignItems: "center", gap: 6 }}>
                {deleting ? <Loader size={14} className="spin" /> : <Trash2 size={14} />}
                Eliminar {selected.size}
              </button>
            )}
            <button className="btn-icon" onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        {error && (
          <div style={{ margin: "0 24px", padding: "8px 12px", borderRadius: 8, background: "#FEF2F2", color: "#DC2626", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 6 }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div style={{ padding: "8px 24px 24px", overflowX: "auto", maxHeight: "65vh", overflowY: "auto" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
              <Loader size={28} className="spin" opacity={0.4} />
            </div>
          ) : responses.length === 0 ? (
            <div className="empty-state">
              <BarChart2 size={40} opacity={0.3} />
              <p>Todavia no hay respuestas para este formulario.</p>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr>
                  {isAdmin && (
                    <th style={{ width: 32, padding: "8px 10px", borderBottom: "1px solid var(--border)" }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} title="Seleccionar todas" />
                    </th>
                  )}
                  {fields.map((field) => (
                    <Fragment key={field.id}>
                      <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--text-muted)", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {field.label}{field.sensitive ? " 🔒" : ""}
                      </th>
                      {hasDateCalculation(field) && (
                        <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--primary)", fontWeight: 600, whiteSpace: "nowrap" }}>
                          {calculatedColumnLabel(field)}
                        </th>
                      )}
                    </Fragment>
                  ))}
                  <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--text-muted)", fontWeight: 600, whiteSpace: "nowrap" }}>
                    Fecha
                  </th>
                  {isAdmin && <th style={{ width: 40, borderBottom: "1px solid var(--border)" }} />}
                </tr>
              </thead>
              <tbody>
                {responses.map((response) => (
                  <tr key={response.id} style={selected.has(response.id) ? { background: "rgba(220,38,38,0.05)" } : undefined}>
                    {isAdmin && (
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)" }}>
                        <input type="checkbox" checked={selected.has(response.id)} onChange={() => toggleSelected(response.id)} />
                      </td>
                    )}
                    {fields.map((field) => {
                      const key = `${response.id}:${field.id}`;
                      const value = response.answers[field.id];
                      return (
                        <Fragment key={field.id}>
                          <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>
                            <CellValue
                              value={value}
                              field={field}
                              revealed={revealed.has(key)}
                              onToggle={() => toggleReveal(key)}
                            />
                          </td>
                          {hasDateCalculation(field) && (
                            <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", color: "var(--primary)", fontWeight: 500 }}>
                              {calculateDateValue(value?.value, field.dateCalculation, response.submitted_at) || <span style={{ color: "var(--text-muted)" }}>—</span>}
                            </td>
                          )}
                        </Fragment>
                      );
                    })}
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                      {formatDate(response.submitted_at)}
                    </td>
                    {isAdmin && (
                      <td style={{ padding: "4px 6px", borderBottom: "1px solid var(--border)" }}>
                        <button className="btn-icon" onClick={() => deleteResponses([response.id])} disabled={deleting}
                          title="Eliminar respuesta" style={{ color: "#DC2626" }}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>
      {ConfirmUI}
    </motion.div>
  );
}
